"use client";

import { useCallback } from "react";
import { MAX_PAGE_PER_PDF } from "../../GroupCandidates/constant";
import { toaster } from "@/_components/lib/ui/toaster";
import { useTranslations } from "next-intl";

let pdfjsPromise: Promise<typeof import("pdfjs-dist")> | null = null;

function getPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import("pdfjs-dist").then((pdfjsLib) => {
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
      return pdfjsLib;
    });
  }
  return pdfjsPromise;
}

interface PdfConversionOptions {
  isThumbnail?: boolean;
  scale?: number;
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
}

export const usePdfToImageConverter = () => {
  const t = useTranslations("space.talent_pool.upload");

  const convertPdfToImage = useCallback(
    async (pdfFile: File, options: PdfConversionOptions = {}) => {
      try {
        const {
          isThumbnail = false,
          scale = isThumbnail ? 0.2 : 1.0,
          quality = isThumbnail ? 0.5 : 0.95,
          maxWidth = isThumbnail ? 300 : 1200,
          maxHeight = isThumbnail ? 300 : 1200,
        } = options;

        const pdfjsLib = await getPdfjs();
        const pdfUrl = URL.createObjectURL(pdfFile);
        const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale });

        // Adjust scale if dimensions exceed maxWidth/maxHeight
        let finalScale = scale;
        if (viewport.width > maxWidth || viewport.height > maxHeight) {
          const widthRatio = maxWidth / viewport.width;
          const heightRatio = maxHeight / viewport.height;
          finalScale = scale * Math.min(widthRatio, heightRatio);
        }

        const finalViewport = page.getViewport({ scale: finalScale });

        const canvas = document.createElement("canvas");
        canvas.width = finalViewport.width;
        canvas.height = finalViewport.height;
        const context = canvas.getContext("2d");

        if (!context) {
          throw new Error("Failed to get canvas context");
        }

        await page.render({
          canvasContext: context,
          viewport: finalViewport,
        }).promise;

        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob(
            (blob) => {
              resolve(blob as Blob);
            },
            "image/jpeg",
            quality,
          );
        });

        const fileName = isThumbnail
          ? `${pdfFile.name.replace(".pdf", "")}_thumbnail.jpg`
          : `${pdfFile.name.replace(".pdf", "")}_original.jpg`;

        const imageFile = new File([blob], fileName, {
          type: "image/jpeg",
          lastModified: pdfFile.lastModified,
        });

        URL.revokeObjectURL(pdfUrl);

        if (pdf.numPages > MAX_PAGE_PER_PDF) {
          const tooManyPagesDescription = t("pdf_too_many_pages_description", {
            fileName: pdfFile.name,
            maxPages: MAX_PAGE_PER_PDF,
          });

          toaster.error({
            title: t("pdf_too_many_pages_title"),
            description: tooManyPagesDescription,
          });
          throw new Error(tooManyPagesDescription);
        }

        return imageFile;
      } catch (error) {
        console.error("Error converting PDF to image:", error);
        throw new Error("Failed to convert PDF to image");
      }
    },
    [t],
  );

  return { convertPdfToImage };
};
